import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { env } from '@/config/env';

/**
 * Shape mínimo de um erro Problem Details (RFC 7807) que a API devolve.
 */
export interface ProblemDetails {
  type: string;
  title: string;
  status: number;
  detail: string;
  instance: string;
  requestId?: string;
  errors?: Array<{ field?: string; message: string }>;
}

/**
 * Estado de autenticação acessado pelo cliente HTTP.
 * O Zustand store se registra aqui via `registerAuthBridge()` para
 * evitar import cíclico entre store e client.
 */
interface AuthBridge {
  getAccessToken: () => string | null;
  setAccessToken: (token: string, expiresAt: string) => void;
  clear: () => void;
  isRefreshing: boolean;
}

const bridge: AuthBridge = {
  getAccessToken: () => null,
  setAccessToken: () => {},
  clear: () => {},
  isRefreshing: false,
};

export function registerAuthBridge(impl: Partial<AuthBridge>) {
  Object.assign(bridge, impl);
}

/** Detecta qual plataforma do cliente (mobile/web) para o header X-Client-Platform. */
const CLIENT_PLATFORM = 'web';

/** Cria instância Axios com interceptors padrão. */
function createClient(): AxiosInstance {
  const client = axios.create({
    baseURL: env.apiUrl,
    withCredentials: true, // cookies httpOnly do refresh token
    headers: {
      Accept: 'application/json',
      'X-Client-Platform': CLIENT_PLATFORM,
    },
    timeout: 30000,
  });

  // Request: injeta Authorization Bearer se houver accessToken em memória
  client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = bridge.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  /*
   * Response: em caso de 401, tenta refresh uma vez e refaz o request original.
   * Fila de requests concorrentes evita disparar N refreshes simultâneos.
   */
  let refreshPromise: Promise<string | null> | null = null;

  const refreshAccessToken = async (): Promise<string | null> => {
    if (refreshPromise) return refreshPromise;
    refreshPromise = (async () => {
      try {
        const { data } = await axios.post(
          `${env.apiUrl}/auth/refresh`,
          {},
          {
            withCredentials: true,
            headers: { 'X-Client-Platform': CLIENT_PLATFORM },
          },
        );
        const payload = data?.data;
        if (payload?.accessToken && payload?.accessTokenExpiresAt) {
          bridge.setAccessToken(payload.accessToken, payload.accessTokenExpiresAt);
          return payload.accessToken as string;
        }
        return null;
      } catch {
        bridge.clear();
        return null;
      } finally {
        refreshPromise = null;
      }
    })();
    return refreshPromise;
  };

  client.interceptors.response.use(
    (res) => res,
    async (error: AxiosError<ProblemDetails>) => {
      const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
      const status = error.response?.status;
      const url = original?.url ?? '';

      // Rotas de auth não devem disparar refresh (podem retornar 401 legítimo)
      const isAuthRoute =
        url.includes('/auth/login') ||
        url.includes('/auth/register-tenant') ||
        url.includes('/auth/refresh') ||
        url.includes('/auth/forgot-password');

      if (status === 401 && !original._retry && !isAuthRoute) {
        original._retry = true;
        const newToken = await refreshAccessToken();
        if (newToken) {
          original.headers = original.headers ?? {};
          original.headers.Authorization = `Bearer ${newToken}`;
          return client(original);
        }
        bridge.clear();
      }
      return Promise.reject(error);
    },
  );

  return client;
}

export const api = createClient();

/** Extrai uma mensagem humana de um erro Axios para exibir em toast/form. */
export function extractErrorMessage(err: unknown): string {
  if (axios.isAxiosError<ProblemDetails>(err)) {
    const problem = err.response?.data;
    if (problem?.detail) return problem.detail;
    if (problem?.title) return problem.title;
    if (err.code === 'ERR_NETWORK') return 'Não foi possível conectar ao servidor.';
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return 'Ocorreu um erro inesperado.';
}

/** Extrai erros de validação por campo (do array `errors` do Problem Details). */
export function extractFieldErrors(err: unknown): Record<string, string> {
  if (axios.isAxiosError<ProblemDetails>(err) && err.response?.data?.errors) {
    const out: Record<string, string> = {};
    for (const e of err.response.data.errors) {
      if (e.field) out[e.field] = e.message;
    }
    return out;
  }
  return {};
}
